// src/CountdownTimerBars.js
import React, { useMemo, useRef, useEffect, useState } from "react";

/**
 * Shared helpers
 */
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function getTotalTime(q) {
  const t = Number(q?.time ?? q?.TIME ?? 0);
  return Number.isFinite(t) && t > 0 ? t : 0;
}

/**
 * A thin, dark “track” the wick runs inside.
 * This component is kept as it is used for specific slides like BREAK TIME.
 */
export function WickTimerBar({ q, timer, top = false, offset = 0 }) {
  const total = getTotalTime(q);
  if (total <= 0) return null;

  const progress = useMemo(() => {
    const remaining = Math.max(0, Number(timer || 0));
    const elapsed = Math.max(0, total - remaining);
    return clamp01(elapsed / total);
  }, [timer, total]);

  const flamePct = progress * 100;

  const explodeRef = useRef(false);
  useEffect(() => {
    if (timer === 0) {
      explodeRef.current = true;
      const t = setTimeout(() => (explodeRef.current = false), 600);
      return () => clearTimeout(t);
    }
  }, [timer]);

  return (
    <div
      className="ctb-wrap"
      style={{
        position: top ? "fixed" : "relative",
        top: top ? (4 + offset) : undefined,
        left: 0,
        width: "100vw",
        zIndex: 1100,
        pointerEvents: "none",
        display: "flex",
        justifyContent: "center",
      }}
      role="presentation"
      aria-hidden
    >
      <div
        className="ctb-wick-rail"
        style={{
          position: "relative",
          width: "min(1080px, 94vw)",
          height: 18,
          borderRadius: 14,
          background: "linear-gradient(180deg, rgba(18,18,22,0.95), rgba(15,15,19,0.86))",
          boxShadow: "inset 0 2px 4px rgba(255,255,255,.08), inset 0 -2px 4px rgba(0,0,0,.55), 0 2px 14px rgba(0,0,0,.5)",
          overflow: "hidden",
        }}
      >
        <div
          className="ctb-wick-fill"
          style={{
            position: "absolute",
            top: 3,
            left: 3,
            height: 12,
            width: `calc(${clamp01(progress) * 100}% - 6px)`,
            borderRadius: 9,
            background: "linear-gradient(90deg, #5a2f14 0%, #ce7f36 36%, #ffb24d 68%, #ffd27a 100%)",
            boxShadow: "inset 0 1px 2px rgba(255,255,255,.15), inset 0 -1px 2px rgba(0,0,0,.45)",
            transition: "width .15s linear",
          }}
        />
        <div
          className="ctb-flame"
          style={{
            position: "absolute",
            top: -10,
            left: `calc(${flamePct}% - 10px)`,
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: "radial-gradient(40% 40% at 40% 40%, #ffd88a 0%, #ffb62b 50%, #8a4a14 100%)",
            boxShadow: "0 0 16px 4px rgba(255,160,32,.65), 0 0 10px 2px rgba(255,220,120,.55)",
            animation: "ctbFlameFlicker .12s infinite alternate ease-in-out",
            transform: "translateZ(0)",
            willChange: "transform, box-shadow, left",
          }}
        />
        <div
          className="ctb-sparks"
          style={{
            position: "absolute",
            top: -7,
            left: `calc(${flamePct}% - 20px)`,
            width: 34,
            height: 8,
            pointerEvents: "none",
            background: "radial-gradient(closest-side, rgba(255,180,64,.8), rgba(255,180,64,0))",
            filter: "blur(1px)",
            opacity: 0.65,
            animation: "ctbSparks .28s infinite alternate",
          }}
        />
        {explodeRef.current && (
          <div
            className="ctb-explosion"
            style={{
              position: "absolute",
              top: -18,
              right: -10,
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,240,170,1) 0%, rgba(255,180,60,.9) 35%, rgba(255,120,0,.65) 60%, rgba(255,120,0,0) 70%)",
              animation: "ctbExplosion .55s ease-out forwards",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
      <div
        className="ctb-seconds"
        style={{
          position: "absolute",
          left: "calc(3vw + 12px)",
          top: -4 + (offset || 0),
          transform: "translateY(-100%)",
          borderRadius: 22,
          padding: "6px 16px 7px",
          fontWeight: 900,
          fontFamily: "'Montserrat', system-ui, Arial, sans-serif",
          fontSize: "clamp(.8rem, 2.1vw, 1.4rem)",
          color: "#111",
          background: "linear-gradient(180deg, #ffe7a6 0%, #ffd76b 55%, #ffc53a 100%)",
          boxShadow: "0 4px 14px rgba(0,0,0,.28), inset 0 1px 2px rgba(255,255,255,.65), inset 0 -2px 4px rgba(0,0,0,.22)",
          textShadow: "0 1px 0 #fff7",
        }}
      >
        {Math.max(0, Number(timer || 0))}s
      </div>
      <style>{`
        @keyframes ctbFlameFlicker {
          0%   { transform: translate(-.8px, -.6px) scale(1.00); box-shadow: 0 0 16px 4px rgba(255,160,32,.6); }
          100% { transform: translate(.6px, .8px) scale(1.06);  box-shadow: 0 0 18px 6px rgba(255,180,40,.75); }
        }
        @keyframes ctbSparks {
          0%   { opacity: .42; transform: translateX(-2px) }
          100% { opacity: .78; transform: translateX(1px) }
        }
        @keyframes ctbExplosion {
          0%   { transform: scale(.3); opacity: .9; filter: blur(.2px); }
          50%  { transform: scale(2.4); opacity: 1;  filter: blur(.2px); }
          100% { transform: scale(3.1); opacity: 0;  filter: blur(1.2px); }
        }
      `}</style>
    </div>
  );
}

/**
 * REPLACEMENT: This is the powerful, fiery timer bar from Quizscreen.js.
 * It is now shared here and includes the `scale` prop for resizing.
 */
export function ThermoTimerBar({ q, timer, questionNumber = 0, scale = 1 }) {
  const total = Math.max(1, parseInt(q.time ?? q.TIME ?? 0, 10) || 0);

  // smooth in-sync motion anchored to last tick
  const anchorTimerRef = useRef(timer);
  const anchorTsRef = useRef(performance.now());
  const prevRemainRef = useRef(timer);

  const [pct, setPct] = useState((timer / total) * 100);
  const [boom, setBoom] = useState(false);
  const [boomPct, setBoomPct] = useState(0);
  const [inferno, setInferno] = useState(false);

  // key by question to reset visuals
  const qKey = useMemo(() => {
    const id = q?.id ?? q?.ID ?? q?.uuid ?? q?.UUID ?? q?.key ?? q?.Q ?? q?.question ?? "";
    return `${id}|${q?.time ?? q?.TIME ?? ""}`;
  }, [q]);

  const elapsed = Math.max(0, total - Math.max(0, timer));
  const showTimer = elapsed >= 5 && timer > 0;

  // reset on question change (NOT every tick)
  useEffect(() => {
    setBoom(false);
    setInferno(false);
    setBoomPct(0);
    setPct((timer / total) * 100);
    anchorTimerRef.current = timer;
    anchorTsRef.current = performance.now();
    prevRemainRef.current = timer;
  }, [qKey, total, timer]);

  // re-anchor each parent tick
  useEffect(() => {
    anchorTimerRef.current = timer;
    anchorTsRef.current = performance.now();
  }, [timer]);

  // rAF loop (continuous)
  useEffect(() => {
    let raf;
    const loop = () => {
      const elapsedMs = performance.now() - anchorTsRef.current;
      const remaining = Math.max(0, anchorTimerRef.current - elapsedMs / 1000);

      if (prevRemainRef.current > 0 && remaining <= 0) {
        setBoomPct((prevRemainRef.current / total) * 100);
        setBoom(true);
        setInferno(true);
        setTimeout(() => setBoom(false), 1200);
        setTimeout(() => setInferno(false), 1200);
      }
      prevRemainRef.current = remaining;

      setPct((remaining / total) * 100);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  // Helper to apply scaling to CSS shadow strings
  const scaleShadow = (shadow) => {
    if (!shadow || scale === 1) return shadow;
    return shadow.replace(/(-?\d*\.?\d+)px/g, (match, n) => `${(parseFloat(n) * scale).toFixed(2)}px`);
  };

  return (
    <>
      {boom && (
        <>
          <div style={{ position: "absolute", top: "50%", left: `calc(${boomPct}% * 1)`, transform: "translate(-50%,-50%)", width: 480 * scale, height: 480 * scale, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,.99), rgba(255,238,0,.97) 34%, rgba(255,175,0,.88) 66%, rgba(255,90,0,.68) 78%, rgba(0,0,0,0) 80%)", animation: "boomFlash 700ms ease-out forwards", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: "50%", left: `calc(${boomPct}% * 1)`, width: 84 * scale, height: 84 * scale, borderRadius: "50%", border: `${12 * scale}px solid rgba(255,210,0,.98)`, transform: "translate(-50%,-50%)", animation: "boomRing 1600ms ease-out forwards", pointerEvents: "none" }} />
          {Array.from({ length: 220 }).map((_, i) => {
            const angle = i * (360 / 220);
            const r = (440 + (i % 9) * 28) * scale;
            const tx = Math.cos((angle * Math.PI) / 180) * r + "px";
            const ty = Math.sin((angle * Math.PI) / 180) * -r * 0.72 + "px";
            return <div key={`sh-${i}`} style={{ position: "absolute", top: "50%", left: `calc(${boomPct}% * 1)`, width: (i % 5 === 0 ? 26 : 18) * scale, height: 4 * scale, borderRadius: 3 * scale, background: "linear-gradient(90deg,#ffd166,#ff8a00)", transformOrigin: "left center", animation: "boomShard 1500ms ease-out forwards", animationName: "boomShard", animationDuration: "1500ms", animationTimingFunction: "ease-out", animationFillMode: "forwards", "--r": `${angle}deg`, "--tx": tx, "--ty": ty, pointerEvents: "none" }} />;
          })}
        </>
      )}

      <div style={{ position: "fixed", left: 0, right: 0, bottom: "5vh", zIndex: 9000, pointerEvents: "none", padding: "0 2vw", "--pct": pct, "--boom": boomPct }}>
        <style>{`
          @keyframes sparkPulse { 0%{transform:scale(1);filter:brightness(1)}100%{transform:scale(1.08);filter:brightness(1.25)} }
          @keyframes emberFloat { 0%{transform:translate(0,0) scale(1);opacity:1}100%{transform:translate(-${18 * scale}px,-${24 * scale}px) scale(.6);opacity:0} }
          @keyframes sparkDrift { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(var(--dx),var(--dy));opacity:0} }
          @keyframes boomRing { 0%{transform:translate(-50%,-50%) scale(.25);opacity:.98}100%{transform:translate(-50%,-50%) scale(5.6);opacity:0} }
          @keyframes boomShard { 0% { transform: translate(-50%,-50%) rotate(var(--r)) scale(1); opacity:1; } 100% { transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) rotate(var(--r)) scale(.7); opacity:0; } }
          @keyframes boomFlash { 0%{opacity:.98}100%{opacity:0} }
          @keyframes infernoFlicker { 0%,100% { opacity:.88 } 40% { opacity:1 } 70% { opacity:.92 } }
          @keyframes infernoFadeOut { 0% { opacity:1 } 100% { opacity:0 } }
        `}</style>

        <div style={{ position: "relative", height: 56 * scale, transform: "perspective(900px) rotateX(7deg)" }}>
          <div style={{ position: "absolute", inset: 0, borderRadius: 28 * scale, background: "linear-gradient(180deg,#151a2b 0%, #0f1424 55%, #0a0f1e 100%)", boxShadow: scaleShadow("0 16px 36px rgba(0,0,0,.55), inset 0 2px 1px rgba(255,255,255,.12), inset 0 -12px 24px rgba(0,0,0,.68)") }} />
          <div style={{ position: "absolute", inset: 0, borderRadius: 28 * scale, background: "linear-gradient(180deg, rgba(255,255,255,.16), rgba(255,255,255,0) 42%, rgba(0,0,0,.28) 72%)" }} />
          {Array.from({ length: 10 }).map((_, i) => <div key={i} style={{ position: "absolute", top: 8 * scale, bottom: 8 * scale, left: `${((i + 1) / 11) * 100}%`, width: 2 * scale, background: "rgba(255,255,255,.12)" }} />)}
          <div style={{ position: "absolute", top: 4 * scale, bottom: 4 * scale, left: 4 * scale, right: 4 * scale, borderRadius: 24 * scale, overflow: "visible" }}>
            <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `calc(${pct}%)`, borderRadius: 24 * scale, background: "linear-gradient(90deg,#ef4444 0%, #fb923c 25%, #facc15 50%, #a3e635 75%, #22c55e 100%)", boxShadow: scaleShadow("inset 0 2px 6px rgba(255,255,255,.35), inset 0 -6px 10px rgba(0,0,0,.35)") }} />
            {questionNumber > 0 && (showTimer || elapsed < 5) && (
              <div style={{ position: "absolute", top: "50%", left: 12 * scale, transform: "translateY(-50%)", pointerEvents: "none", zIndex: 5 }}>
                <div style={{ minWidth: 64 * scale, height: 34 * scale, padding: `0 ${14 * scale}px`, borderRadius: 20 * scale, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 18 * scale, color: "#fff", textShadow: scaleShadow("0 2px 6px #000a"), background: "linear-gradient(180deg,#b91c1c,#7a0f0f)", boxShadow: scaleShadow("inset 0 2px 4px rgba(255,255,255,.18), inset 0 -4px 8px rgba(0,0,0,.40), 0 4px 16px rgba(0,0,0,.45)") }}>
                  {showTimer ? `${Math.ceil(timer)}s` : `Q${questionNumber}`}
                </div>
              </div>
            )}
            {!boom && pct > 0 && (
              <>
                <div style={{ position: "absolute", top: `calc(50% - ${30 * scale}px)`, left: `calc(${pct}% - 1.5%)`, transform: "translate(-50%,-50%)", width: 64 * scale, height: 64 * scale, borderRadius: "50%", background: "radial-gradient(circle at 32% 32%, #fff8e8, #ffd166 45%, #ff8a00 65%, #b91c1c 90%)", boxShadow: scaleShadow("0 0 28px 14px rgba(255,136,0,.82), 0 0 54px 22px rgba(255,72,0,.48)"), animation: "sparkPulse .1s ease-in-out infinite alternate" }} />
                {Array.from({ length: 22 }).map((_, i) => {
                  const back = (28 + i * 6) * scale;
                  return <div key={i} style={{ position: "absolute", top: `calc(50% - ${(18 + i * 3) * scale}px)`, left: `calc(${pct}%)`, transform: `translate(calc(-50% - ${back}px), -50%)`, width: (i % 4 === 0 ? 8 : 6) * scale, height: (i % 4 === 0 ? 8 : 6) * scale, borderRadius: "50%", background: "radial-gradient(circle, #ffd166, #ff8a00)", boxShadow: scaleShadow("0 0 14px 4px rgba(255,136,0,.65)"), animation: "emberFloat .75s ease-out infinite", animationDelay: `${i * 0.06}s` }} />;
                })}
                {Array.from({ length: 30 }).map((_, i) => {
                  const angle = (i * 12) * Math.PI / 180;
                  const dx = Math.cos(angle) * (70 + (i % 5) * 16) * scale;
                  const dy = -Math.sin(angle) * (48 + (i % 7) * 12) * scale;
                  return <div key={`hs-${i}`} style={{ position: "absolute", top: `calc(50% - ${20 * scale}px)`, left: `calc(${pct}%)`, transform: "translate(-50%, -50%)", width: 5 * scale, height: 5 * scale, borderRadius: "50%", background: "radial-gradient(circle, #ffe08a, #ff9300)", boxShadow: scaleShadow("0 0 12px 4px rgba(255,140,0,.55)"), animation: "sparkDrift 900ms ease-out forwards", "--dx": `${dx}px`, "--dy": `${dy}px` }} />;
                })}
              </>
            )}
            {boom && (
              <>
                <div style={{ position: "absolute", top: "50%", left: `calc(${boomPct}%)`, transform: "translate(-50%,-50%)", width: 420 * scale, height: 420 * scale, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,.99), rgba(255,238,0,.96) 34%, rgba(255,170,0,.88) 66%, rgba(255,90,0,.65) 78%, rgba(0,0,0,0) 80%)", animation: "boomFlash 700ms ease-out forwards", pointerEvents: "none" }} />
                <div style={{ position: "absolute", top: "50%", left: `calc(${boomPct}%)`, width: 76 * scale, height: 76 * scale, borderRadius: "50%", border: `${12 * scale}px solid rgba(255,210,0,.98)`, transform: "translate(-50%,-50%)", animation: "boomRing 1600ms ease-out forwards", pointerEvents: "none" }} />
                {Array.from({ length: 200 }).map((_, i) => {
                  const angle = i * (360 / 200);
                  const r = (420 + (i % 9) * 26) * scale;
                  const tx = Math.cos((angle * Math.PI) / 180) * r + "px";
                  const ty = Math.sin((angle * Math.PI) / 180) * -r * 0.72 + "px";
                  return <div key={`sh-${i}`} style={{ position: "absolute", top: "50%", left: `calc(${boomPct}%)`, width: (i % 5 === 0 ? 24 : 16) * scale, height: 4 * scale, borderRadius: 3 * scale, background: "linear-gradient(90deg,#ffd166,#ff8a00)", transformOrigin: "left center", animation: "boomShard 1500ms ease-out forwards", "--r": `${angle}deg`, "--tx": tx, "--ty": ty, pointerEvents: "none" }} />;
                })}
              </>
            )}
            {inferno && <div style={{ position: "absolute", inset: "-40% -8%", background: "radial-gradient(ellipse at center, rgba(255,210,0,.98), rgba(255,120,0,.94) 58%, rgba(0,0,0,0) 75%)", filter: "blur(10px)", mixBlendMode: "screen", animation: "infernoFlicker 320ms ease-in-out infinite, infernoFadeOut 1000ms ease-out 200ms forwards" }} />}
          </div>
        </div>
      </div>
    </>
  );
}